import { ACTIVE_ORGANIZATION_ID_COOKIE } from '@lib/constants/cookies';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import { GetProductListsV2Response } from '@lib/types/api/ProductListV2';
import Cookies from 'js-cookie';
import { useMemo } from 'react';
import useSWR from 'swr';

type OrganizationsResponse = {
  organizations: Organization[];
  defaultOrganizationId: string;
};

export type ListsData = {
  personalLists: GetProductListsV2Response;
  orgLists: GetProductListsV2Response;
  orgListIds: Set<string>;
  activeOrganization: Organization | null;
  organizationId: string | undefined;
  isLoading: boolean;
  mutatePersonalLists: () => Promise<unknown>;
  mutateOrgLists: () => Promise<unknown>;
};

export function useListsData(shouldFetch: boolean): ListsData {
  const { data: orgData } = useSWR<OrganizationsResponse>(
    shouldFetch ? '/api/users/organizations' : null,
    failingFetcher
  );

  const activeOrganization = useMemo(() => {
    const organizations = orgData?.organizations ?? [];
    if (organizations.length === 0) return null;
    const storedOrgId = Cookies.get(ACTIVE_ORGANIZATION_ID_COOKIE);
    return (
      organizations.find((org) => org.id === storedOrgId) ??
      organizations.find((org) => org.id === orgData?.defaultOrganizationId) ??
      organizations[0]
    );
  }, [orgData]);

  const organizationId = activeOrganization?.id;

  const { data: personalData, mutate: mutatePersonalLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch ? '/api/lists' : null,
      failingFetcher
    );

  const { data: orgListsData, mutate: mutateOrgLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch && organizationId
        ? `/api/organizations/${organizationId}/lists`
        : null,
      failingFetcher
    );

  const personalLists = useMemo(() => personalData ?? [], [personalData]);
  const orgLists = useMemo(() => orgListsData ?? [], [orgListsData]);
  const orgListIds = useMemo(
    () => new Set(orgLists.map((list) => list.id)),
    [orgLists]
  );

  return {
    personalLists,
    orgLists,
    orgListIds,
    activeOrganization,
    organizationId,
    isLoading:
      shouldFetch && (!personalData || (!!organizationId && !orgListsData)),
    mutatePersonalLists,
    mutateOrgLists,
  };
}
